/**
 * Classification of a failed OAuth token refresh (#26615).
 *
 * `ionic-appauth` deletes the stored token response on *every* refresh error, no matter whether
 * the refresh token was actually rejected or the device simply had no network for a moment
 * (see `AuthService.refreshToken()` in the library). Both cases end up in the same
 * `RefreshFailed` action, so the app cannot tell an expired grant from a dead zone.
 *
 * The input contract is narrower than `unknown` suggests: `AuthActionBuilder.RefreshFailed`
 * reduces the rejection to `error.message` before anyone sees it, so what arrives here is a
 * plain string — or `undefined`, when the rejection had no message. With the CapacitorHttp
 * requestor (`NgHttpService`) the real payloads are:
 *
 * | situation            | action.error                                             | result        |
 * |----------------------|----------------------------------------------------------|---------------|
 * | grant rejected       | 'invalid_grant' (AppAuthError.message = response.error)  | invalid-grant |
 * | iOS offline          | 'The Internet connection appears to be offline.'         | transient     |
 * | request timeout      | 'The request timed out.'                                 | transient     |
 * | discovery offline    | 'Unable To Obtain Server Configuration'                  | transient     |
 * | DNS failure          | 'Unable to resolve host "…"'                             | unknown       |
 * | shapeless rejection  | undefined                                                | unknown       |
 *
 * This classifier restores the distinction the library flattens:
 * - `invalid-grant`: the authorization server rejected the refresh token → logging out is correct.
 * - `transient`: network/timeout/discovery failure → the session is still valid, retrying is correct.
 * - `unknown`: nothing recognisable. Treated as transient by callers on purpose: keeping a user
 *   logged in one cycle too long is cheap, throwing away a valid session is not. The DNS case
 *   above lands here deliberately — the default rule carries it.
 */
export type RefreshErrorKind = 'invalid-grant' | 'transient' | 'unknown';

/**
 * Only a rejected grant is terminal. The other OAuth error codes of RFC 6749 §5.2
 * (`invalid_client`, `unauthorized_client`, `unsupported_grant_type`, `invalid_scope`) describe
 * a broken *request* or a broken *client configuration* — the user's refresh token is entirely
 * intact, and discarding it would turn our own misconfiguration (a wrong `client_id` rollout, a
 * broken IdP setup) into a forced password login for the whole user base.
 */
const TERMINAL_OAUTH_ERROR_CODES = ['invalid_grant'];

/** Markers of a failed request rather than a rejected grant. */
const TRANSIENT_MARKERS = [
  'network',
  'timeout',
  'timed out',
  'failed to fetch',
  'load failed',
  'offline',
  'connection',
  'econnreset',
  'econnrefused',
  'enotfound',
  'etimedout',
  'net::',
  'err_internet',
  'err_network',
  'err_connection',
  'unable to obtain server configuration',
];

/**
 * `AppAuthError.message` carries `response.error` verbatim, so a rejected grant arrives as the
 * bare OAuth code. The match is therefore anchored to the start of the message — a substring
 * match would let a network error that merely *mentions* a code end a session.
 */
function isTerminalOauthCode(text: string): boolean {
  return TERMINAL_OAUTH_ERROR_CODES.some(
    code => text === code || text.startsWith(`${code} `) || text.startsWith(`${code}:`)
  );
}

function toText(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  // Tolerates a caller handing over the error object itself instead of the action's message
  // string. Anything else yields '' and with it 'unknown' — the designed safe default.
  if (error != null && typeof (error as { message?: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }
  return '';
}

/**
 * Classifies a refresh failure. Never throws — a diagnostic must not add a second failure
 * on top of the one it is describing.
 */
export function classifyRefreshError(error: unknown): RefreshErrorKind {
  try {
    const text = toText(error).trim().toLowerCase();

    if (isTerminalOauthCode(text)) {
      return 'invalid-grant';
    }
    if (TRANSIENT_MARKERS.some(marker => text.includes(marker))) {
      return 'transient';
    }

    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/** True when the session should be kept and the refresh retried. */
export function isRecoverableRefreshError(error: unknown): boolean {
  return classifyRefreshError(error) !== 'invalid-grant';
}
